import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i1$1 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i1 from 'keycloak-angular';
import { KeycloakService, KeycloakAngularModule, KeycloakAuthGuard } from 'keycloak-angular';
export * from 'keycloak-angular';
import * as i2 from 'ngx-permissions';
import { NgxPermissionsGuard, NgxPermissionsModule } from 'ngx-permissions';
export * from 'ngx-permissions';
import { from, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { __awaiter } from 'tslib';

/**
 * @description - Auth Interceptor adds the token from keycloak to the header for the api endpoints.
 */
class AuthInterceptor {
    constructor(keycloakService) {
        this.keycloakService = keycloakService;
    }
    /**
     * @description - gets the user permissions token from the keycloak service
     */
    getToken() {
        return from(this.keycloakService.getToken());
    }
    /**
     * @description - intercepts every http call to run the addAuthorizationHeader.
     * @param req - Http Request
     * @param next - http handler
     */
    intercept(req, next) {
        return this.addAuthorizationHeader(req, next);
    }
    /**
     * @description - sets the user permissions token to the header for the api endpoint calls.
     * @param request - http request
     * @param next - http handler
     */
    addAuthorizationHeader(request, next) {
        return this.getToken().pipe(switchMap(token => {
            request = request.clone({
                setHeaders: {
                    Authorization: 'bearer ' + token
                }
            });
            return next.handle(request);
        }));
    }
}
AuthInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthInterceptor, deps: [{ token: i1.KeycloakService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.KeycloakService }]; } });

const AuthHttpInterceptorProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
};

class AuthService {
    constructor(keycloakService, permissionsService, rolesService) {
        this.keycloakService = keycloakService;
        this.permissionsService = permissionsService;
        this.rolesService = rolesService;
    }
    logout() {
        this.keycloakService.logout();
    }
    getToken() {
        return from(this.keycloakService.getToken());
    }
    getExpiryTimestamp() {
        const auth = this.keycloakService.getKeycloakInstance();
        return auth.refreshTokenParsed && auth.refreshTokenParsed.exp;
    }
    getUserRoles() {
        return this.keycloakService.getUserRoles(false);
    }
    get isAuthenticated() {
        return this.keycloakService.getKeycloakInstance().authenticated;
    }
    get displayName() {
        return this._keycloakProfile
            ? `${this._keycloakProfile.firstName} ${this._keycloakProfile.lastName}`
            : this.keycloakService.getUsername();
    }
    startTimer(td) {
        const exp = timer(td).subscribe(() => {
            this.logout();
        });
    }
    validateExpiry() {
        const expDate = new Date(this.getExpiryTimestamp() * 1000);
        let alertDate = new Date();
        let td = expDate.getTime() - alertDate.getTime();
        console.warn('Token expirying in ', td / 1000 / 60);
        this.startTimer(td);
    }
    isUserInRole(role) {
        return this.keycloakService.isUserInRole(role);
    }
    loadPermissions() {
        this.permissionsService.loadPermissions(this.getUserRoles());
    }
    createPermissionsGuard(router) {
        return new NgxPermissionsGuard(this.permissionsService, this.rolesService, router);
    }
    loadProfileData() {
        return from(this.keycloakService.loadUserProfile(false).then((p) => {
            this._keycloakProfile = p;
            return this._keycloakProfile;
        }));
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthService, deps: [{ token: i1.KeycloakService }, { token: i2.NgxPermissionsService }, { token: i2.NgxRolesService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.KeycloakService }, { type: i2.NgxPermissionsService }, { type: i2.NgxRolesService }]; } });

const KUP_AUTH_CONFIG = new InjectionToken('auth.config');

class AppConfig {
    constructor(config) {
        this.config = config;
        return config;
    }
}
AppConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AppConfig, deps: [{ token: KUP_AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
AppConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AppConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: AppConfig, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [KUP_AUTH_CONFIG]
                    }] }];
    } });
/**
 * @description - initialiser to run on keycloak init to set the configuration and options. If environment is develop,
 *  stores the keycloak tokens in local storage.
 * @param keycloak - keycloak service
 */
function initializer(keycloak, appConfig, auth) {
    const config = appConfig.keycloakConfig;
    const initOptions = appConfig.keycloakInitOptions;
    return () => {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield keycloak
                    .init({
                    config,
                    initOptions,
                    bearerExcludedUrls: ['/api'],
                })
                    .then(() => {
                    auth.loadPermissions();
                });
                resolve(undefined);
            }
            catch (error) {
                reject(error);
            }
        }));
    };
}

class KupAuthModule {
    static forRoot(options) {
        return {
            ngModule: KupAuthModule,
            providers: [
                AuthHttpInterceptorProvider,
                {
                    provide: KUP_AUTH_CONFIG,
                    useFactory: options.useFactory,
                    deps: options.deps,
                },
                AppConfig,
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializer,
                    multi: true,
                    deps: [KeycloakService, AppConfig, AuthService],
                },
            ],
        };
    }
}
KupAuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: KupAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
KupAuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.2", ngImport: i0, type: KupAuthModule, imports: [HttpClientModule,
        KeycloakAngularModule, i2.NgxPermissionsModule], exports: [HttpClientModule, KeycloakAngularModule, NgxPermissionsModule] });
KupAuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: KupAuthModule, providers: [RouterModule], imports: [HttpClientModule,
        KeycloakAngularModule,
        NgxPermissionsModule.forRoot(), HttpClientModule, KeycloakAngularModule, NgxPermissionsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: KupAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        KeycloakAngularModule,
                        NgxPermissionsModule.forRoot(),
                    ],
                    exports: [HttpClientModule, KeycloakAngularModule, NgxPermissionsModule],
                    providers: [RouterModule],
                }]
        }] });

/**
 * @description - this guard is call on the routes where the user authentication needs to be checked. Used in the
 *  individual component routes in the CanActivate property.
 */
class CanAuthenticationGuard extends KeycloakAuthGuard {
    constructor(router, keycloakAngular) {
        super(router, keycloakAngular);
        this.router = router;
        this.keycloakAngular = keycloakAngular;
    }
    /**
     * @description - method to check if the user has the correct user role to access, and if not, redirects to the
     *  log in screen.
     * @param route - activated route snapshot
     * @param state - router state snapshot
     */
    isAccessAllowed(route, state) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            if (!this.authenticated) {
                this.keycloakAngular.login();
                return reject(false);
            }
            const requiredRoles = route.data.roles;
            if (!requiredRoles || requiredRoles.length === 0) {
                return resolve(true);
            }
            else {
                if (!this.roles || this.roles.length === 0) {
                    resolve(false);
                }
                resolve(requiredRoles.every(role => this.roles.indexOf(role) > -1));
            }
        }));
    }
}
CanAuthenticationGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: CanAuthenticationGuard, deps: [{ token: i1$1.Router }, { token: i1.KeycloakService }], target: i0.ɵɵFactoryTarget.Injectable });
CanAuthenticationGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: CanAuthenticationGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.2", ngImport: i0, type: CanAuthenticationGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: i1.KeycloakService }]; } });

class AppSettings {
}
class AppSettingsService {
    constructor() { }
    getConfig(type, src) {
        switch (type) {
            case 'auth':
                const authSettings = {
                    configOptions: src.keycloakConfigOptions,
                    initOptions: src.keycloakInitOptions
                };
                return authSettings;
            default:
                throw new Error('Invalid setting type');
        }
    }
}

/*
 * Public API Surface of kup.auth library
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppSettings, AppSettingsService, AuthService, CanAuthenticationGuard, KupAuthModule };
